type $TSFixMeKey<T> = keyof T;
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable quotes */
/* eslint-disable fp/no-loops */
/* eslint-disable no-param-reassign */
/* eslint-disable complexity */
/**
 * forked from json-typedef-js
 * Module schema provides code to safely handle JSON Typedef schemas.
 *
 * JSON Typedef schemas are represented in this module with {@link Schema}, and
 * you can check if a JSON object is a correct schema by using {@link isSchema}
 * and {@link isValidSchema}.
 *
 * @packageDocumentation
 */

/**
 * Schema is a TypeScript representation of a correct JSON Typedef schema.
 *
 * The JSON Typedef specification allows schemas to take on one of eight forms.
 * Each of those forms has its own type in this module; Schema is simply a union
 * of each of those eight types.
 */
export type Schema =
  | SchemaFormEmpty
  | SchemaFormRef
  | SchemaFormType
  | SchemaFormEnum
  | SchemaFormElements
  | SchemaFormProperties
  | SchemaFormValues
  | SchemaFormDiscriminator;

/**
 * SchemaFormEmpty represents schemas of the empty form.
 */
export type SchemaFormEmpty = SharedFormProperties;

/**
 * SchemaFormRef represents schemas of the ref form.
 */
export type SchemaFormRef = SharedFormProperties & {
  ref: string;
};

/**
 * SchemaFormType represents schemas of the type form.
 */
export type SchemaFormType = SharedFormProperties & {
  type: Type;
};

/**
 * Type represents the legal values of the "type" keyword in JSON Typedef.
 */
export type Type =
  | 'boolean'
  | 'float32'
  | 'float64'
  | 'int8'
  | 'uint8'
  | 'int16'
  | 'uint16'
  | 'int32'
  | 'uint32'
  | 'string'
  | 'timestamp';

/**
 * SchemaFormEnum represents schemas of the enum form.
 */
export type SchemaFormEnum = SharedFormProperties & {
  enum: string[];
};

/**
 * SchemaFormElements represents schemas of the elements form.
 */
export type SchemaFormElements = SharedFormProperties & {
  elements: Schema;
};

/**
 * SchemaFormProperties represents schemas of the properties form.
 */
export type SchemaFormProperties = SharedFormProperties &
  (
    | {
        properties?: { [name: string]: Schema };
        optionalProperties: { [name: string]: Schema };
        additionalProperties?: boolean;
      }
    | {
        properties: { [name: string]: Schema };
        optionalProperties?: { [name: string]: Schema };
        additionalProperties?: boolean;
      }
  );

/**
 * SchemaFormValues represents schemas of the values form.
 */
export type SchemaFormValues = SharedFormProperties & {
  values: Schema;
};

/**
 * SchemaFormDiscriminator represents schemas of the discriminator form.
 */
export type SchemaFormDiscriminator = SharedFormProperties & {
  discriminator: string;
  mapping: { [name: string]: Schema };
};

/**
 * SharedFormProperties contains the properties shared among all schema forms.
 */
interface SharedFormProperties {
  definitions?: { [definition: string]: Schema };
  metadata?: {
    // on root schema
    formats?: Record<
      string,
      {
        description: string;
        validation:
          | {
              type: 'regex';
              regex: string;
            }
          | { type: 'web_url' };
      }
    >;
    /** what schema features must be supported to use this schema */
    features?: string[];
    /** on any schema. Value must be a valid format key from root schema */
    format?: string;
    /** example keys to be used next to reference of this schema in doc*/
    examples?: string[];
    /** prosemirror like node patter. Only applicable to 'elements' form */
    pattern?: {
      value: string;
      description: string;
    };
    validation?: {
      /** for arrays */
      minSize?: number;
      maxSize?: number;
      /** for strings */
      minLength?: number;
      maxLength?: number;
      /** for ints */
      min?: number;
      max?: number;
      /** only items with unique tags might be in array */
      uniqueTags?: boolean;
    };

    /** Valid only for 'properties' form, works like in protobufs. Should contain names from optionalProperties of which only one should exist */
    oneOf?: {
      strict?: boolean;
      properties: string[];
    };

    /** documentation section */
    section?: string;
    /** Description is markdown */
    description?: string;
    /** Record of types used in schema vs their tag values (tag being the key, and type being value) */
    refUnion?: { [tagValue: string]: string };
    /** marks as deprecated in wix-docs and ts-doc */
    deprecated?: boolean;
    /** hides from wix-docs and ts-doc */
    internal?: boolean;
    /** emit enum option for typescript  gen */
    emitEnum?: string;
  };
  nullable?: boolean;
}

/**
 * isEmptyForm checks whether some Schema is of the empty form.
 *
 * @param schema The schema to validate
 */
export function isEmptyForm(schema: Schema): schema is SchemaFormEmpty {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { definitions, nullable, metadata, ...rest } = schema;
  return Object.keys(rest).length === 0;
}

/**
 * isRefForm checks whether some Schema is of the ref form.
 *
 * @param schema The schema to validate
 */
export function isRefForm(schema: Schema): schema is SchemaFormRef {
  return 'ref' in schema;
}

/**
 * isTypeForm checks whether some Schema is of the type form.
 *
 * @param schema The schema to validate
 */
export function isTypeForm(schema: Schema): schema is SchemaFormType {
  return 'type' in schema;
}

/**
 * isEnumForm checks whether some Schema is of the enum form.
 *
 * @param schema The schema to validate
 */
export function isEnumForm(schema: Schema): schema is SchemaFormEnum {
  return 'enum' in schema;
}

/**
 * isElementsForm checks whether some Schema is of the elements form.
 *
 * @param schema The schema to validate
 */
export function isElementsForm(schema: Schema): schema is SchemaFormElements {
  return 'elements' in schema;
}

/**
 * isPropertiesForm checks whether some Schema is of the properties form.
 *
 * @param schema The schema to validate
 */
export function isPropertiesForm(schema: Schema): schema is SchemaFormProperties {
  return 'properties' in schema || 'optionalProperties' in schema;
}

/**
 * isPropertiesForm checks whether some Schema is of the values form.
 *
 * @param schema The schema to validate
 */
export function isValuesForm(schema: Schema): schema is SchemaFormValues {
  return 'values' in schema;
}

/**
 * isDiscriminatorForm checks whether some Schema is of the values form.
 *
 * @param schema The schema to validate
 */
export function isDiscriminatorForm(schema: Schema): schema is SchemaFormDiscriminator {
  return 'discriminator' in schema;
}

/**
 * isValidSchema checks whether some Schema is correct, according to the syntax
 * rules of JSON Typedef.
 *
 * In particular, isValidSchema verifies that:
 *
 * 1. The schema does not have any non-root definitions,
 * 2. All references point to actually-existing definitions,
 * 3. All enums are non-empty, and do not contain duplicates,
 * 4. The `properties` and `optionalProperties` of a schema never share
 *    properties,
 * 5. All schemas in `mapping` are of the properties form,
 * 6. Schemas in `mapping` never re-specify the `discriminator` property
 *
 * If an object returned from `JSON.parse` passes both {@link isSchema} and
 * {@link isValidSchema}, then it is a correct JSON Typedef schema.
 *
 * @param schema The schema to validate
 * @param root The schema to consider as the "root" schema. If undefined,
 * `schema` will be used as the root. This is usually what you want to do.
 */
export function isValidSchema(
  schema: Schema,
  path: string[] = [],
  skipRefCheck = false,
  root?: Schema
): { type: 'ok' } | { type: 'error'; message: string; path: string[] } {
  const err = (message: string) => ({ type: 'error', message, path } as const);
  if (root === undefined) {
    root = schema;
  }

  if (schema.definitions !== undefined) {
    if (root !== schema) {
      return err('non-root schema has definitions');
    }

    for (const subSchemaKey of Object.keys(schema.definitions)) {
      const result = isValidSchema(
        schema.definitions[subSchemaKey],
        path.concat('definitions', subSchemaKey),
        skipRefCheck,
        root
      );
      if (result.type === 'error') {
        return result;
      }
    }
  }

  if (isRefForm(schema) && !skipRefCheck) {
    if (!(schema.ref in (root.definitions || {}))) {
      return err('ref does not point to a definition');
    }
  }

  if (isEnumForm(schema)) {
    if (schema.enum.length === 0) {
      return err('enum must be non-empty');
    }

    if (schema.enum.length !== new Set(schema.enum).size) {
      return err('enum must not contain duplicates');
    }
  }

  if (isElementsForm(schema)) {
    const result = isValidSchema(schema.elements, path.concat('elements'), skipRefCheck, root);
    if (result.type === 'error') {
      return result;
    }
  }

  if (isPropertiesForm(schema)) {
    for (const subSchemaKey of Object.keys(schema.properties || {})) {
      const result = isValidSchema(
        schema.properties![subSchemaKey],
        path.concat('properties', subSchemaKey),
        skipRefCheck,
        root
      );
      if (result.type === 'error') {
        return result;
      }
    }

    for (const subSchemaKey of Object.keys(schema.optionalProperties || {})) {
      const result = isValidSchema(
        schema.optionalProperties![subSchemaKey],
        path.concat('optionalProperties', subSchemaKey),
        skipRefCheck,
        root
      );
      if (result.type === 'error') {
        return result;
      }
    }

    for (const key of Object.keys(schema.properties || {})) {
      if (key in (schema.optionalProperties || {})) {
        return err('properties and optionalProperties must not share properties: ' + key);
      }
    }

    for (const key of Object.keys(schema.optionalProperties || {})) {
      if (key in (schema.properties || {})) {
        return err('properties and optionalProperties must not share properties: ' + key);
      }
    }
  }

  if (isValuesForm(schema)) {
    const result = isValidSchema(schema.values, path.concat('values'), skipRefCheck, root);
    if (result.type === 'error') {
      return result;
    }
  }

  if (isDiscriminatorForm(schema)) {
    for (const subSchemaKey of Object.keys(schema.mapping)) {
      const subSchema = schema.mapping[subSchemaKey];
      const result = isValidSchema(
        subSchema,
        path.concat('mapping', subSchemaKey),
        skipRefCheck,
        root
      );
      if (result.type === 'error') {
        return result;
      }
      if (!isPropertiesForm(subSchema)) {
        return err('mapping values in discriminator form must be of the properties form');
      }

      if (subSchema.nullable) {
        return err('mapping values in discriminator form must not be nullable');
      }

      if (schema.discriminator in (subSchema.properties || {})) {
        return err('mapping values in discriminator form must not re-specify the discriminator');
      }

      if (schema.discriminator in (subSchema.optionalProperties || {})) {
        return err('mapping values in discriminator form must not re-specify the discriminator');
      }
    }
  }

  return ok();
}

// Index of valid form "signatures" -- i.e., combinations of the presence of the
// keywords (in order):
//
// ref type enum elements properties optionalProperties additionalProperties
// values discriminator mapping
//
// The keywords "definitions", "nullable", and "metadata" are not included here,
// because they would restrict nothing.
const VALID_FORMS = [
  // Empty form
  [false, false, false, false, false, false, false, false, false, false],
  // Ref form
  [true, false, false, false, false, false, false, false, false, false],
  // Type form
  [false, true, false, false, false, false, false, false, false, false],
  // Enum form
  [false, false, true, false, false, false, false, false, false, false],
  // Elements form
  [false, false, false, true, false, false, false, false, false, false],
  // Properties form -- properties or optional properties or both, and never
  // additional properties on its own
  [false, false, false, false, true, false, false, false, false, false],
  [false, false, false, false, false, true, false, false, false, false],
  [false, false, false, false, true, true, false, false, false, false],
  [false, false, false, false, true, false, true, false, false, false],
  [false, false, false, false, false, true, true, false, false, false],
  [false, false, false, false, true, true, true, false, false, false],
  // Values form
  [false, false, false, false, false, false, false, true, false, false],
  // Discriminator form
  [false, false, false, false, false, false, false, false, true, true],
];

// List of valid values that the "type" keyboard may take on.
const VALID_TYPES = [
  'boolean',
  'float32',
  'float64',
  'int8',
  'uint8',
  'int16',
  'uint16',
  'int32',
  'uint32',
  'string',
  'timestamp',
];

const ok = () => ({ type: 'ok' } as const);
/**
 * isSchema checks whether some piece of JSON data has the shape of a JSON
 * Typedef Schema.
 *
 * This function only looks at the "shape" of data: it just makes sure all
 * property names and types are valid, and that the data takes on one of the
 * eight JSON Typedef forms.
 *
 * If an object returned from `JSON.parse` passes both {@link isSchema} and
 * {@link isValidSchema}, then it is a correct JSON Typedef schema.
 *
 * @param data The data to check
 */
export function isSchema(
  data: unknown,
  path: string[] = []
): { type: 'ok' } | { type: 'error'; message: string; path: string[] } {
  const err = (message: string) => ({ type: 'error', message, path } as const);

  if (typeof data !== 'object' || Array.isArray(data) || data === null) {
    return err('schema must be an object');
  }

  // TypeScript does not let us coerce `{}` into `{ [index: string]: unknown }`.
  // At the time of writing, it's unclear why this is the case, nor under what
  // circumstances such an coercion would be wrong.
  //
  // So we work around the compiler here.
  const obj: { [index: string]: unknown } = data as any;

  const {
    definitions = undefined,
    nullable = undefined,
    metadata = undefined,
    ref = undefined,
    type = undefined,
    enum: enum_ = undefined,
    elements = undefined,
    properties = undefined,
    optionalProperties = undefined,
    additionalProperties = undefined,
    values = undefined,
    discriminator = undefined,
    mapping = undefined,
    ...rest
  } = obj;

  const formSignature = [
    ref !== undefined,
    type !== undefined,
    enum_ !== undefined,
    elements !== undefined,
    properties !== undefined,
    optionalProperties !== undefined,
    additionalProperties !== undefined,
    values !== undefined,
    discriminator !== undefined,
    mapping !== undefined,
  ];

  let formOk = false;
  for (const validForm of VALID_FORMS) {
    formOk = formOk || validForm.every((value, index) => value === formSignature[index]);
  }

  if (!formOk) {
    return err("schema doesn't match any valid form signature");
  }

  if (definitions !== undefined) {
    if (typeof definitions !== 'object' || Array.isArray(definitions) || definitions === null) {
      return err('definitions must be an object');
    }

    for (const key of Object.keys(definitions)) {
      const result = isSchema(
        definitions[key as $TSFixMeKey<typeof definitions>],
        path.concat('definitions', key)
      );
      if (result.type === 'error') {
        return result;
      }
    }
  }

  if (nullable !== undefined) {
    if (typeof nullable !== 'boolean') {
      return err('nullable must be a boolean');
    }
  }

  if (metadata !== undefined) {
    if (typeof metadata !== 'object' || Array.isArray(metadata) || metadata === null) {
      return err('metadata must be an object');
    }
  }

  if (ref !== undefined) {
    if (typeof ref !== 'string') {
      return err('ref must be a string');
    }
  }

  if (type !== undefined) {
    if (typeof type !== 'string' || !VALID_TYPES.includes(type)) {
      return err('type must be a string and one of the valid types');
    }
  }

  if (enum_ !== undefined) {
    if (!Array.isArray(enum_)) {
      return err('enum must be an array');
    }

    if (!enum_.every(elem => typeof elem === 'string')) {
      return err('enum must be an array of strings');
    }
  }

  if (elements !== undefined) {
    const result = isSchema(elements, path.concat('elements'));
    if (result.type === 'error') {
      return result;
    }
  }

  if (properties !== undefined) {
    if (typeof properties !== 'object' || Array.isArray(properties) || properties === null) {
      return err('properties must be an object');
    }

    for (const key of Object.keys(properties)) {
      const result = isSchema(
        properties[key as $TSFixMeKey<typeof properties>],
        path.concat('properties', key)
      );
      if (result.type === 'error') {
        return result;
      }
    }
  }

  if (optionalProperties !== undefined) {
    if (
      typeof optionalProperties !== 'object' ||
      Array.isArray(optionalProperties) ||
      optionalProperties === null
    ) {
      return err('optionalProperties must be an object');
    }

    for (const key of Object.keys(optionalProperties)) {
      const result = isSchema(
        optionalProperties[key as $TSFixMeKey<typeof optionalProperties>],
        path.concat('optionalProperties', key)
      );
      if (result.type === 'error') {
        return result;
      }
    }
  }

  if (additionalProperties !== undefined) {
    if (typeof additionalProperties !== 'boolean') {
      return err('additionalProperties must be a boolean');
    }
  }

  if (values !== undefined) {
    const result = isSchema(values, path.concat('values'));
    if (result.type === 'error') {
      return result;
    }
  }

  if (discriminator !== undefined) {
    if (typeof discriminator !== 'string') {
      return err('discriminator must be a string');
    }
  }

  if (mapping !== undefined) {
    if (typeof mapping !== 'object' || Array.isArray(mapping) || mapping === null) {
      return err('mapping must be an object');
    }

    for (const key of Object.keys(mapping)) {
      const result = isSchema(
        mapping[key as $TSFixMeKey<typeof mapping>],
        path.concat('mapping', key)
      );
      if (result.type === 'error') {
        return result;
      }
    }
  }

  if (Object.keys(rest).length !== 0) {
    return err('schema has unknown properties');
  }

  return ok();
}
